import './styles/graphic.scss';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import proj4 from 'proj4';
import districts from './data/districts.json';
import metaData from './data/meta.json';
import { politics } from './utils/colors';

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    // width,
    // height
  } = props;

  // Load state projection data, city data, and redistricting data
  Promise.all([
    d3.csv('./state-sillo-projections.csv'),
    d3.csv('./us-major-cities.csv'),
    d3.json('https://ix.cnn.io/data/redistricting/redistricting-states-data.json'),
  ])
    .then(([projectionData, citiesData, redistrictingData]) => {
      // Create lookup object for state projections and register proj4 definitions
      const stateProjections = {};
      projectionData.forEach((row) => {
        stateProjections[row.ST] = row.PROJ;

        // Register the proj4 definition if it exists
        if (row.PROJ4) {
          proj4.defs(row.PROJ, row.PROJ4);
        }
      });

      // Initialize the visualization with the fetched data
      initVisualization(redistrictingData, stateProjections, citiesData);
    })
    .catch(() => {
      // Initialize with empty data if fetch fails
      initVisualization([], {}, []);
    });

  function initVisualization(statesData, stateProjections, citiesData) {
    // Filter to states with enacted, pending, or under discussion status
    const filteredStates = statesData.filter(d => d.status && ['enacted', 'pending', 'under discussion'].includes(d.status.toLowerCase()));

    // Separate states by status
    const enactedStates = filteredStates.filter(d => d.status.toLowerCase() === 'enacted');
    const pendingStates = filteredStates.filter(d => d.status.toLowerCase() === 'pending');
    const underDiscussionStates = filteredStates.filter(d => d.status.toLowerCase() === 'under discussion');

    // Sort "under discussion" states alphabetically by state name
    underDiscussionStates.sort((a, b) => a.state.localeCompare(b.state));

    // Combine: enacted, pending, then alphabetically sorted under discussion
    const displayStates = [...enactedStates, ...pendingStates, ...underDiscussionStates];

    // Create lookup object for easy access
    const redistrictingData = {};
    displayStates.forEach((stateData) => {
      redistrictingData[stateData.state_abbr] = stateData;
    });

    // Helper function to create projection for a state
    const createProjection = (stateAbbr) => {
      const epsgCode = stateProjections[stateAbbr];

      if (!epsgCode) {
        return d3.geoAlbers();
      }

      try {
        const proj4Projection = proj4(epsgCode);

        const rawProjection = (lon, lat) => {
          try {
            const lonDeg = lon * 180 / Math.PI;
            const latDeg = lat * 180 / Math.PI;
            const result = proj4Projection.forward([lonDeg, latDeg]);

            if (!result || !isFinite(result[0]) || !isFinite(result[1])) {
              return null;
            }
            return result;
          } catch (e) {
            return null;
          }
        };

        rawProjection.invert = (x, y) => {
          try {
            const result = proj4Projection.inverse([x, y]);
            if (!result || !isFinite(result[0]) || !isFinite(result[1])) {
              return null;
            }
            return [result[0] * Math.PI / 180, result[1] * Math.PI / 180];
          } catch (e) {
            return null;
          }
        };

        return d3.geoProjection(rawProjection)
          .scale(1)
          .translate([0, 0]);
      } catch (error) {
        return d3.geoAlbers();
      }
    };

    // Helper function to manually fit projection to bounds
    // This avoids the fitSize issue where D3 tests coordinates outside the projection's valid domain
    const fitProjectionToBounds = (projection, features, width, height) => {
      // Calculate bounds in projected space
      let xMin = Infinity; let yMin = Infinity; let xMax = -Infinity; let
        yMax = -Infinity;
      let validPointCount = 0;

      features.forEach((feature) => {
        const coords = feature.geometry.coordinates;
        const processCoords = (coordArray) => {
          if (typeof coordArray[0] === 'number') {
            // Single coordinate pair
            const projected = projection([coordArray[0], coordArray[1]]);
            if (projected && isFinite(projected[0]) && isFinite(projected[1])) {
              // Additional check: reject extremely large values that might indicate projection issues
              if (Math.abs(projected[0]) < 1e10 && Math.abs(projected[1]) < 1e10) {
                xMin = Math.min(xMin, projected[0]);
                xMax = Math.max(xMax, projected[0]);
                yMin = Math.min(yMin, projected[1]);
                yMax = Math.max(yMax, projected[1]);
                validPointCount++;
              }
            }
          } else {
            // Nested array
            coordArray.forEach(processCoords);
          }
        };
        processCoords(coords);
      });

      // If we didn't get valid points, something is wrong
      if (validPointCount === 0 || !isFinite(xMin) || !isFinite(xMax) || !isFinite(yMin) || !isFinite(yMax)) {
        projection.scale(1).translate([width / 2, height / 2]);
        return projection;
      }

      // Calculate scale and translate
      const projectedWidth = xMax - xMin;
      const projectedHeight = yMax - yMin;
      const scale = 0.95 / Math.max(projectedWidth / width, projectedHeight / height);
      const translateX = (width - scale * (xMin + xMax)) / 2;
      const translateY = (height - scale * (yMin + yMax)) / 2;

      projection.scale(scale).translate([translateX, translateY]);

      return projection;
    };
    // Create controls section
    const controlsDiv = container.append('div')
      .attr('class', 'controls');

    // State dropdown
    const dropdownDiv = controlsDiv.append('div')
      .attr('class', 'dropdown-container');

    dropdownDiv.append('label')
      .attr('for', 'state-select')
      .text('Select State: ');

    const dropdown = dropdownDiv.append('select')
      .attr('id', 'state-select')
      .attr('class', 'state-select');

    // Add default placeholder option
    dropdown.append('option')
      .attr('value', '')
      .attr('selected', true)
      .text('State');

    // Add options for each state
    displayStates.forEach((stateData, i) => {
      dropdown.append('option')
        .attr('value', i)
        .text(stateData.state);
    });

    // Add swipe icon to the far right
    const swipeSvg = controlsDiv.append('svg')
      .attr('class', 'swipe-icon')
      .attr('viewBox', '0 0 512 512')
      .attr('xmlns', 'http://www.w3.org/2000/svg');

    swipeSvg.append('path')
      .attr('d', 'M200.242 274.913C200.379 269.902 200.248 264.848 200.248 259.832L200.231 105.45C200.228 85.2818 200.859 65.881 226.159 63.3507C239.24 62.4835 252.587 70.9688 255.518 84.1309C256.741 89.6184 256.458 95.3892 256.46 100.979L256.457 179.283C259.39 177.816 262.252 176.188 265.406 175.233C280.862 170.556 295.85 180.368 300.258 195.326C321.921 184.607 338.425 194.433 344.479 216.623C361.132 204.887 384.706 214.295 387.69 234.09C388.608 240.182 388.034 246.938 388.042 253.112C388.083 285.234 385.436 364.168 363.86 389.479C360.303 393.652 355.726 396.599 353.656 401.911C347.777 417.004 360.508 419.198 335.806 433.987C313.154 447.549 283.677 451.435 258.155 444.997C244.444 441.857 228.409 435.617 218.145 425.737C212.643 420.441 213.214 415.919 213.079 408.987C212.893 399.503 208.595 397.637 202.902 391.04C187.136 372.771 180.775 359.389 169.9 338.259C161.546 322.025 142.516 288.334 126.857 277.699C120.287 273.238 113.792 272.8 113.903 262.862C114.072 247.754 132.194 238.613 145.435 238.82C171.226 239.222 184.617 258.937 200.242 274.913ZM226.159 75.0665C209.012 78.2719 211.666 93.3195 211.67 106.395L211.683 261.79C211.682 270.271 213.988 284.48 202.4 286.521C186.138 289.384 173.423 246.279 141.886 250.81C136.648 251.563 130.075 254.799 126.831 259.08C122.837 264.35 130.028 265.325 133.294 267.597C164.472 289.278 184.561 350.89 211.229 382.468C224.204 397.834 224.821 393.765 225.341 415.812C250.189 440.573 298.538 441.179 328.225 424.315C332.438 421.922 337.551 418.976 340.484 415.004C340.237 408.592 340.745 401.977 342.968 395.912C347.157 385.567 355.73 383.641 359.917 373.434C373.565 340.164 376.192 290.017 376.262 254.238C376.284 242.62 378.979 229.433 365.675 224.076C357.553 220.806 348.454 225.885 345.444 233.736C342.616 241.112 347.555 260.234 341.107 262.92C332.167 266.643 332.442 254.806 332.46 249.76L332.51 230.5C332.535 220.549 334.124 209.885 323.392 204.678C314.699 200.461 302.204 204.988 301.202 215.512C300.894 218.75 301.135 222.13 301.168 225.383L301.238 241.193C301.25 245.153 302.069 250.79 297.38 252.535C288.006 256.023 289.148 243.164 289.188 238.493L289.222 217.471C289.223 212.118 289.986 201.709 288.321 196.897C283.676 183.477 264.756 182.195 258.354 194.619C255.808 199.561 256.424 205.275 256.441 210.642L256.482 227.554C256.48 231.954 257.131 236.592 251.98 238.202C241.144 241.587 244.826 214.584 244.819 209.157L244.822 103.408C244.821 98.3783 245.454 91.6374 244.14 86.8388C242.126 79.4825 233.732 73.9075 226.159 75.0665Z');

    swipeSvg.append('path')
      .attr('d', 'M134.875 134.273C137.578 136.831 148.096 145.66 149.216 148.155C151.358 152.922 146.471 158.502 141.673 156.144C139.877 155.261 116.536 133.535 114.165 130.863C107.622 123.491 122.469 114.036 127.534 109.021C131.368 105.224 138.323 96.3562 143.356 94.7938C148.255 94.6807 151.863 98.9123 149.054 103.649C146.068 108.684 137.416 114.204 133.222 120.594L132.953 121.007C135.408 120.636 137.932 120.986 140.403 120.995L170.293 120.996C175.309 120.951 183.679 119.273 184.011 126.625C184.396 135.186 167.592 132.65 162.535 132.651L132.953 132.641L134.875 134.273Z');

    swipeSvg.append('path')
      .attr('d', 'M380.059 121.007C376.71 117.453 373.065 114.123 369.525 110.759C364.895 106.359 357.225 98.1136 368.424 95.811C373.843 96.659 385.265 109.841 389.33 113.76C395.024 119.248 404.713 124.466 397.036 131.932L380.713 147.66C377.878 150.403 374.949 153.945 371.671 156.095C367.671 158.718 362.126 155.473 362.659 150.575C363.04 147.071 376.956 135.003 380.059 132.641L330.312 132.641L297.524 132.657C293.568 132.661 279.592 134.045 276.637 131.363C274.303 129.245 274.196 124.76 276.478 122.565C279.182 119.965 289.61 121.005 293.169 121.004L380.059 121.007Z');

    // Create wrapper for container with fade gradients
    const containerWrapper = container.append('div')
      .attr('class', 'container-wrapper');

    const containerDiv = containerWrapper.append('div')
      .attr('class', 'container');

    if (metaData.footnote) {
      container.append('div')
        .attr('class', 'data-note')
        .append('p')
        .attr('class', 'footnote')
        .html(metaData.footnote);
    }

    // Function to render map for a state
    const renderMap = (stateDiv, stateAbbr, isNew, toggleControls, preserveTransform = null) => {
      const mapContainer = stateDiv.select('.map-container');

      // Store current zoom transform if one exists and we're not explicitly preserving one
      let currentTransform = preserveTransform;
      if (!currentTransform) {
        const existingSvg = mapContainer.select('svg');
        if (!existingSvg.empty()) {
          currentTransform = d3.zoomTransform(existingSvg.node());
        }
      }

      // Clear only the SVG, keep the toggle if it exists
      mapContainer.selectAll('svg, .zoom-controls').remove();

      // Get dynamic width from container
      const containerWidth = mapContainer.node().getBoundingClientRect().width;
      const width = containerWidth || 430; // fallback to 430 if container width is 0
      const height = 280;

      const svg = mapContainer.append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('class', 'state-map');

      const mapGroup = svg.append('g').attr('class', 'map-group');
      const g = mapGroup.append('g');
      const citiesGroup = mapGroup.append('g').attr('class', 'cities-group');

      // Check if state has 2025 data
      const has2025Data = districts.objects['districts-2025']
        && topojson.feature(districts, districts.objects['districts-2025'])
          .features.some(f => f.properties.STATE_ABBR === stateAbbr);

      // If requesting new map but no 2025 data exists, force to 2024
      const useNewMap = isNew && has2025Data;
      const topoKey = useNewMap ? 'districts-2025' : 'districts-2024';
      const geojson = topojson.feature(districts, districts.objects[topoKey]);

      // Filter features for this state
      const stateFeatures = geojson.features.filter(f => f.properties.STATE_ABBR === stateAbbr);

      if (stateFeatures.length === 0) {
        svg.append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .attr('font-size', 14)
          .text(`No data for ${stateAbbr}`);
        return;
      }

      // Create projection and path
      // Use custom EPSG projection for each state
      const projection = createProjection(stateAbbr);
      fitProjectionToBounds(projection, stateFeatures, width - 20, height - 20);

      const path = d3.geoPath().projection(projection);

      // Get key districts for highlighting
      const stateData = redistrictingData[stateAbbr];
      let keyDistricts = [];
      let keyDistrictsRep = [];
      let keyDistrictsDem = [];
      let partyColor = '#D6C38E'; // default gold color

      // Check if status is "enacted" to use darker colors
      const isEnacted = stateData && stateData.status && stateData.status.toLowerCase() === 'enacted';
      const repColor = isEnacted ? '#E06969' : '#E06969'; // #F0B7B6
      const demColor = isEnacted ? '#408EC8' : '#408EC8'; // #BCD3EC

      if (useNewMap) {
        // For new map, use key_districts and party_driving_efforts
        if (stateData && stateData.key_districts) {
          // Parse the key_districts string (format: "TX09', 'TX32', 'TX35")
          keyDistricts = stateData.key_districts
            .split(',')
            .map(d => d.trim().replace(/'/g, ''));
        }

        // Determine color based on party_driving_efforts
        if (stateData && stateData.party_driving_efforts) {
          const party = stateData.party_driving_efforts.toLowerCase();
          if (party.includes('republican')) {
            partyColor = repColor;
          } else if (party.includes('democrat')) {
            partyColor = demColor;
          }
        }
      } else {
        // For old map, use key_districts_old_held_rep and key_districts_old_held_dem
        if (stateData && stateData.key_districts_old_held_rep) {
          keyDistrictsRep = stateData.key_districts_old_held_rep
            .split(',')
            .map(d => d.trim().replace(/'/g, ''));
        }
        if (stateData && stateData.key_districts_old_held_dem) {
          keyDistrictsDem = stateData.key_districts_old_held_dem
            .split(',')
            .map(d => d.trim().replace(/'/g, ''));
        }
      }

      // Draw districts
      g.selectAll('path')
        .data(stateFeatures)
        .enter()
        .append('path')
        .attr('d', path)
        .attr('fill', (d) => {
          const distId = d.properties.DIST_ID;

          if (useNewMap) {
            // For new map, use single color for all key districts
            const isKeyDistrict = keyDistricts.includes(distId);
            return isKeyDistrict ? partyColor : '#D0D0D0';
          }
          // For old map, use party-specific colors
          const isRepDistrict = keyDistrictsRep.includes(distId);
          const isDemDistrict = keyDistrictsDem.includes(distId);
          if (isRepDistrict) return repColor; // red (darker if enacted)
          if (isDemDistrict) return demColor; // blue (darker if enacted)
          return '#D0D0D0';
        })
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.75)
        .attr('vector-effect', 'non-scaling-stroke')
        .attr('class', (d) => {
          const distId = d.properties.DIST_ID;
          if (useNewMap) {
            const isKeyDistrict = keyDistricts.includes(distId);
            return isKeyDistrict ? 'district key-district' : 'district';
          }
          const isRepDistrict = keyDistrictsRep.includes(distId);
          const isDemDistrict = keyDistrictsDem.includes(distId);
          return (isRepDistrict || isDemDistrict) ? 'district key-district' : 'district';
        });

      // Draw city labels from CSV data
      if (citiesData) {
        // Get cities to omit from CNN API data
        let citiesToOmit = [];
        if (stateData && stateData.cities_omit) {
          try {
            // Parse cities_omit if it's a string, otherwise use as array
            if (typeof stateData.cities_omit === 'string') {
              // Try JSON parse first
              citiesToOmit = JSON.parse(stateData.cities_omit);
            } else if (Array.isArray(stateData.cities_omit)) {
              citiesToOmit = stateData.cities_omit;
            }
          } catch (e) {
            // If parsing fails, split by comma and strip quotes
            // Format is like: 'Fresno', 'City', 'City2'
            citiesToOmit = stateData.cities_omit
              .split(',')
              .map(c => c.trim().replace(/^['"]|['"]$/g, ''));
          }
        }

        // Filter cities for this state and exclude omitted cities
        const stateCities = citiesData.filter((city) => {
          if (city.STATE_ABBR !== stateAbbr) return false;
          // Check if city name is in the omit list
          return !citiesToOmit.includes(city.Name);
        });

        stateCities.forEach((city) => {
          // Project the coordinates (use longitude, latitude)
          const lon = parseFloat(city.longitude);
          const lat = parseFloat(city.latitude);
          const coords = projection([lon, lat]);

          if (coords && !isNaN(coords[0]) && !isNaN(coords[1])) {
            // Create a group for this city that will have counter-scaling
            const cityGroup = citiesGroup.append('g')
              .attr('class', 'city-group')
              .datum({ x: coords[0], y: coords[1] })
              .attr('transform', `translate(${coords[0]}, ${coords[1]})`);

            // Add city circle
            cityGroup.append('circle')
              .attr('r', 3)
              .attr('fill', '#fff')
              .attr('stroke', '#262626')
              .attr('class', 'city-point');

            // Calculate label position based on CARDINAL direction
            const cardinal = city.CARDINAL || 'NE';
            let dx = 0; let dy = 0; let
              textAnchor = 'start';

            // Set offsets and text-anchor based on cardinal direction
            switch (cardinal) {
              case 'N':
                dx = 0; dy = -5; textAnchor = 'middle';
                break;
              case 'NE':
                dx = 5; dy = -5; textAnchor = 'start';
                break;
              case 'E':
                dx = 5; dy = 3; textAnchor = 'start';
                break;
              case 'SE':
                dx = 5; dy = 10; textAnchor = 'start';
                break;
              case 'S':
                dx = 0; dy = 14; textAnchor = 'middle';
                break;
              case 'SW':
                dx = -5; dy = 10; textAnchor = 'end';
                break;
              case 'W':
                dx = -5; dy = 3; textAnchor = 'end';
                break;
              case 'NW':
                dx = -5; dy = -5; textAnchor = 'end';
                break;
            }

            // Add city label
            cityGroup.append('text')
              .attr('x', dx)
              .attr('y', dy)
              .attr('text-anchor', textAnchor)
              .attr('font-size', '14px')
              .attr('font-weight', '400')
              .attr('fill', '#262626')
              .attr('class', 'city-label')
              .text(city.Name);
          }
        });
      }

      // Center the map group within the SVG
      try {
        const bbox = mapGroup.node().getBBox();
        const centerX = (width - bbox.width) / 2 - bbox.x;
        const centerY = (height - bbox.height) / 2 - bbox.y;
        mapGroup.attr('transform', `translate(${centerX}, ${centerY})`);
      } catch (e) {
        // If getBBox fails, skip centering
      }

      // Track current zoom scale
      let currentScale = 1;

      // Declare button variables that will be used in zoom handler
      let zoomInBtn; let zoomOutBtn; let
        resetBtn;

      // Function to update button states based on zoom level and position
      const updateZoomButtons = (transform) => {
        const scale = transform.k;
        currentScale = scale;
        const isAtMinZoom = scale <= 1.01; // Small threshold for floating point comparison
        const isAtMaxZoom = scale >= 7.99;

        // Check if map has been panned (translated from origin)
        const hasPanned = Math.abs(transform.x) > 0.1 || Math.abs(transform.y) > 0.1;

        // Map has been modified if zoomed or panned
        const hasBeenModified = !isAtMinZoom || hasPanned;

        // Enable/disable buttons
        if (zoomOutBtn && resetBtn && zoomInBtn) {
          zoomOutBtn.property('disabled', isAtMinZoom).classed('disabled', isAtMinZoom);
          resetBtn.property('disabled', !hasBeenModified).classed('disabled', !hasBeenModified);
          zoomInBtn.property('disabled', isAtMaxZoom).classed('disabled', isAtMaxZoom);
        }
      };

      // Add zoom behavior (but disable scroll wheel)
      const zoom = d3.zoom()
        .scaleExtent([1, 8])
        .filter((event) => {
          // Disable scroll wheel zoom, allow button zoom
          // Handle both D3 v5 (d3.event) and v6+ (event parameter)
          const e = event || d3.event;
          return e && e.type !== 'wheel';
        })
        .on('zoom', (event) => {
          // Handle both D3 v5 (d3.event) and v6+ (event parameter)
          const transform = (event && event.transform) || (d3.event && d3.event.transform);
          if (transform) {
            g.attr('transform', transform);

            // Apply transform to cities but counter-scale to keep constant size
            citiesGroup.attr('transform', transform);
            const scale = transform.k;
            citiesGroup.selectAll('.city-group')
              .attr('transform', d => `translate(${d.x}, ${d.y}) scale(${1 / scale})`);

            updateZoomButtons(transform);
          }
        });

      svg.call(zoom);

      // If we have a saved transform, apply it to restore zoom level
      if (currentTransform && currentTransform.k !== 1) {
        svg.call(zoom.transform, currentTransform);
        // Update button states immediately
        updateZoomButtons(currentTransform);
      }

      // Create or reuse map toggle buttons (only create once per state)
      let mapToggle = mapContainer.select('.map-toggle-local');
      let controlsRow = mapContainer.select('.map-controls-row');

      if (controlsRow.empty()) {
        // Create container for toggle and legend OR no proposed map message
        controlsRow = mapContainer.insert('div', ':first-child')
          .attr('class', 'map-controls-row');

        if (has2025Data) {
          // State has 2025 data - show toggle buttons and legend
          mapToggle = controlsRow.append('div')
            .attr('class', 'map-toggle-local');

          const newMapBtn = mapToggle.append('button')
            .attr('class', 'toggle-btn-local active')
            .text('New map')
            .on('click', function () {
              if (!d3.select(this).classed('active')) {
                mapToggle.select('.toggle-btn-local').classed('active', false);
                d3.select(this).classed('active', true);
                renderMap(stateDiv, stateAbbr, true, mapToggle);
              }
            });

          const oldMapBtn = mapToggle.append('button')
            .attr('class', 'toggle-btn-local')
            .text('Old map')
            .on('click', function () {
              if (!d3.select(this).classed('active')) {
                mapToggle.select('.toggle-btn-local').classed('active', false);
                d3.select(this).classed('active', true);
                renderMap(stateDiv, stateAbbr, false, mapToggle);
              }
            });

          // Add legend inline with toggle
          const mapLegend = controlsRow.append('div')
            .attr('class', 'map-legend');

          mapLegend.append('span')
            .attr('class', 'legend-color');

          mapLegend.append('span')
            .attr('class', 'legend-label')
            .text('Targeted districts');
        } else {
          // State doesn't have 2025 data - show "No proposed map yet" message
          controlsRow.append('div')
            .attr('class', 'no-new-map-message')
            .text('⏳ New maps have not been drawn');
        }
      }

      // Update active state based on isNew (only if toggle exists)
      if (!mapToggle.empty()) {
        mapToggle.selectAll('.toggle-btn-local')
          .classed('active', (d, i) => (i === 0 && isNew) || (i === 1 && !isNew));
      }

      // Update legend colors dynamically based on map type
      const mapLegend = controlsRow.select('.map-legend');
      if (!mapLegend.empty()) {
        // Clear existing legend colors and wrapper
        mapLegend.selectAll('.legend-color').remove();
        mapLegend.selectAll('.legend-colors-wrapper').remove();

        if (useNewMap) {
          // For new map, show single party color
          mapLegend.insert('span', '.legend-label')
            .attr('class', 'legend-color')
            .style('background-color', partyColor);

          mapLegend.select('.legend-label')
            .text('Targeted districts');
        } else {
          // For old map, always show both red and blue diamonds
          const legendColorsWrapper = mapLegend.insert('div', '.legend-label')
            .attr('class', 'legend-colors-wrapper');

          legendColorsWrapper.append('span')
            .attr('class', 'legend-color legend-color-rep')
            .style('background-color', repColor);

          legendColorsWrapper.append('span')
            .attr('class', 'legend-color legend-color-dem')
            .style('background-color', demColor);

          mapLegend.select('.legend-label')
            .text('Current party of House rep.');
        }
      }

      // Create zoom controls container
      const controlsDiv = mapContainer.append('div')
        .attr('class', 'zoom-controls');

      // Determine initial button state based on current transform
      const hasBeenModified = currentTransform
                             && (currentTransform.k > 1.01
                              || Math.abs(currentTransform.x) > 0.1
                              || Math.abs(currentTransform.y) > 0.1);

      // Zoom in button
      zoomInBtn = controlsDiv.append('button')
        .attr('class', 'zoom-btn zoom-in')
        .text('+')
        .on('click', () => {
          svg.transition().duration(300).call(zoom.scaleBy, 1.8);
        });

      // Zoom out button
      zoomOutBtn = controlsDiv.append('button')
        .attr('class', 'zoom-btn zoom-out')
        .text('−')
        .property('disabled', !hasBeenModified)
        .classed('disabled', !hasBeenModified)
        .on('click', () => {
          svg.transition().duration(300).call(zoom.scaleBy, 0.5);
        });

      // Reset button with state silhouette
      resetBtn = controlsDiv.append('button')
        .attr('class', 'zoom-btn reset-btn')
        .attr('title', `Reset ${stateAbbr} view`)
        .property('disabled', !hasBeenModified)
        .classed('disabled', !hasBeenModified)
        .on('click', () => {
          svg.transition().duration(500).call(zoom.transform, d3.zoomIdentity);
        });

      // Create mini SVG for state silhouette in reset button
      const miniSvg = resetBtn.append('svg')
        .attr('width', 20)
        .attr('height', 20)
        .attr('viewBox', '0 0 20 20');

      // Create a simplified projection for the mini silhouette
      const miniProjection = createProjection(stateAbbr);
      fitProjectionToBounds(miniProjection, stateFeatures, 18, 18);

      const miniPath = d3.geoPath().projection(miniProjection);

      // Draw state outline (union of all districts)
      miniSvg.append('path')
        .datum({ type: 'FeatureCollection', features: stateFeatures })
        .attr('d', miniPath)
        .attr('class', 'state-silhouette')
        .attr('fill', 'currentColor')
        .attr('transform', 'translate(1, 1)');
    };

    // Auto-scroll control functions (defined early so state divs can use them)
    let animationId;
    let isUserInteracting = false;
    let isProgrammaticScroll = false;
    let isStateSelected = false; // Track if a state is selected from dropdown

    // Detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                     || window.innerWidth < 700;

    const scrollSpeed = isMobile ? 0.5 : 0.2; // pixels per frame
    let scrollDirection = 1; // 1 for right (forward), -1 for left (backward)
    // Float scroll position: many browsers round scrollLeft to integers, so += 0.2 never advances
    // if we read scrollLeft back each frame. Track position here for smooth sub-pixel motion.
    let autoScrollPosition = null;
    const containerNode = containerDiv.node();

    // Disable smooth scrolling for auto-scroll to work properly
    containerNode.style.scrollBehavior = 'auto';

    const stopAutoScroll = (permanent = false) => {
      isUserInteracting = true;
      // Permanently disable autoscroll after any interaction if specified
      if (permanent) {
        isStateSelected = true; // Reuse this flag to permanently disable
      }
    };

    let resumeTimeout;
    const resumeAutoScroll = () => {
      // Don't resume if a state has been explicitly selected from dropdown
      if (isStateSelected) {
        return;
      }

      clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(() => {
        // Double-check that a state hasn't been selected before resuming
        if (!isStateSelected) {
          isUserInteracting = false;
          autoScrollPosition = null; // resync from DOM after pause
        }
      }, 2000); // Resume after 2 seconds of no interaction
    };

    // Helper function to format seat change
    const formatSeatChange = (stateData) => {
      // Check if failed is true
      const isFailed = stateData.failed === true || stateData.failed === 'true' || stateData.failed === 'TRUE';

      // Check if seat data is missing or empty
      const hasRepSeats = stateData.rep_seats && stateData.rep_seats.trim() !== '';
      const hasDemSeats = stateData.dem_seats && stateData.dem_seats.trim() !== '';

      // If no seat data, show "Seats TBD" with party_driving_efforts color
      if (!hasRepSeats && !hasDemSeats) {
        const partyDriving = stateData.party_driving_efforts ? stateData.party_driving_efforts.toLowerCase() : '';
        let party = 'neutral pending';

        if (partyDriving.includes('republican')) {
          party = isFailed ? 'failed' : 'rep pending';
        } else if (partyDriving.includes('democratic') || partyDriving.includes('democrat')) {
          party = isFailed ? 'failed' : 'dem pending';
        } else if (isFailed) {
          party = 'failed';
        }

        return {
          text: 'Seats TBD',
          party,
          showFailedBadge: isFailed,
        };
      }

      const repSeats = parseInt(stateData.rep_seats) || 0;
      const demSeats = parseInt(stateData.dem_seats) || 0;

      if (demSeats > 0) {
        return {
          text: `+${demSeats} Dem. seat${demSeats > 1 ? 's' : ''}`,
          party: isFailed ? 'failed' : 'dem',
          showFailedBadge: isFailed,
        };
      } if (repSeats > 0) {
        return {
          text: `+${repSeats} Rep. seat${repSeats > 1 ? 's' : ''}`,
          party: isFailed ? 'failed' : 'rep',
          showFailedBadge: isFailed,
        };
      }
      return {
        text: 'No change',
        party: 'neutral',
        showFailedBadge: false,
      };
    };

    const getFailedStatusLabel = (stateAbbr) => {
      if (stateAbbr === 'NY') return 'Map drafting blocked';
      if (stateAbbr === 'MO' || stateAbbr === 'VA') return 'Map blocked';
      return 'Map failed';
    };

    // Helper function to format status
    const formatStatus = (status, isFailed = false, stateAbbr = '') => {
      const failedLabel = getFailedStatusLabel(stateAbbr);
      const statusMap = {
        enacted: isFailed ? failedLabel : 'Map enacted',
        pending: 'Map pending',
        'under discussion': 'Under discussion',
      };
      return statusMap[status.toLowerCase()] || status;
    };

    // Helper function to create a state div
    const createStateDiv = (stateData, isDuplicate = false) => {
      const stateAbbr = stateData.state_abbr;
      const stateName = stateData.state;
      const stateDiv = containerDiv.append('div')
        .attr('class', 'state-div')
        .attr('data-state', stateAbbr)
        .attr('data-duplicate', isDuplicate ? 'true' : null);

      const seatChange = formatSeatChange(stateData);

      // State header with name and badge
      const headerDiv = stateDiv.append('div')
        .attr('class', 'state-header');

      headerDiv.append('h2')
        .attr('class', 'state-name')
        .text(stateData.state);

      headerDiv.append('span')
        .attr('class', `seat-badge ${seatChange.party}`)
        .text(seatChange.text);

      // Add "Failed" badge if needed (use "Blocked" for New York and Virginia)
      if (seatChange.showFailedBadge) {
        const failedText = ['NY', 'VA', 'MO'].includes(stateData.state_abbr) ? 'Blocked' : 'Failed';
        headerDiv.append('span')
          .attr('class', 'seat-badge failed-label')
          .text(failedText);
      }

      // Status section
      const statusDiv = stateDiv.append('div')
        .attr('class', 'status-section');

      statusDiv.append('h3')
        .attr('class', 'status-title')
        .text('STATUS');

      const progressBar = statusDiv.append('div')
        .attr('class', 'progress-bar');

      // Check if failed is true
      const isFailed = stateData.failed === true || stateData.failed === 'true' || stateData.failed === 'TRUE';

      const failedStatusLabel = getFailedStatusLabel(stateAbbr);

      // Determine statuses array and current index based on state and failure status
      let statuses;
      let currentIndex;

      if (isFailed && stateAbbr === 'NY') {
        // NY failed at "under discussion" stage - show blocked at step 2
        statuses = ['Under discussion', 'Map drafting blocked', 'Map enacted'];
        currentIndex = 1; // Second step (Map drafting blocked)
      } else if (isFailed) {
        // Other failed states - show failure at step 3
        statuses = ['Under discussion', 'Map pending', failedStatusLabel];
        currentIndex = 2; // Third step
      } else {
        // Normal flow
        statuses = ['Under discussion', 'Map pending', 'Map enacted'];
        const statusToUse = stateData.status || 'pending';
        const currentStatus = formatStatus(statusToUse, isFailed, stateAbbr);
        currentIndex = statuses.indexOf(currentStatus);
      }

      statuses.forEach((status, idx) => {
        const statusItem = progressBar.append('div')
          .attr('class', () => {
            let classes = 'status-item';
            if (idx < currentIndex) classes += ' completed';
            if (idx === currentIndex) classes += ' current';
            return classes;
          });

        statusItem.append('div')
          .attr('class', () => {
            let classes = 'status-icon';
            if (idx > currentIndex) classes += ' future';
            return classes;
          })
          .html(() => {
            if (idx !== currentIndex) return '';

            // Show X icon if failed, checkmark otherwise
            if (isFailed) {
              return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.04 851.24">
                <path d="M696.57,601.17l-155.05-155.05,155.05-155.05c26.35-26.35,26.35-69.06,0-95.41h0c-26.35-26.35-69.06-26.35-95.41,0l-155.05,155.05-155.05-155.05c-26.35-26.35-69.06-26.35-95.41,0h0c-26.35,26.35-26.35,69.06,0,95.41l155.05,155.05-155.05,155.05c-26.35,26.35-26.35,69.06,0,95.41h0c26.35,26.35,69.06,26.35,95.41,0l155.05-155.05,155.05,155.05c26.35,26.35,69.06,26.35,95.41,0h0c26.35-26.35,26.35-69.06,0-95.41Z"/>
              </svg>`;
            }

            // Show checkmark for non-failed states
            return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.04 851.24">
              <path d="M337.42,499.02l-120.5-120.5c-25.75-25.75-67.51-25.75-93.26,0h0c-25.75,25.75-25.75,67.5,0,93.25l167.12,167.12c25.75,25.75,67.5,25.75,93.25,0l333.31-333.31c25.75-25.75,25.75-67.5,0-93.25h0c-25.75-25.75-67.51-25.75-93.26,0l-286.68,286.69Z"/>
            </svg>`;
          });

        statusItem.append('div')
          .attr('class', 'status-label')
          .text(status);
      });

      // What to know section
      const whatToKnowDiv = stateDiv.append('div')
        .attr('class', 'what-to-know');

      whatToKnowDiv.append('h3')
        .attr('class', 'section-title')
        .html('👉 WHAT TO KNOW');

      // Create wrapper for description with max-height
      const descriptionWrapper = whatToKnowDiv.append('div')
        .attr('class', 'description-wrapper');

      descriptionWrapper.append('p')
        .attr('class', 'description')
        .html(stateData.what_to_know || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sit amet velit non purus cursus porttitor. Pellentesque tincidunt imperdiet est, ut congue ex auctor vitae. Phasellus egestas eget arcu sed laoreet.');

      // Only show link if link_out exists
      let readMoreLinkContainer;
      if (stateData.link_out) {
        readMoreLinkContainer = whatToKnowDiv
          .append('div')
          .attr('class', 'read-more-link-container');

        readMoreLinkContainer
          .append('a')
          .attr('class', 'read-more-link')
          .attr('href', stateData.link_out)
          .attr('target', '_blank')
          .html('🔗 Read more CNN analysis');
      }

      // Check if content exceeds height and add expand arrow
      const descriptionParagraph = descriptionWrapper.select('.description');
      const checkHeight = () => {
        // Check if text is clamped (scrollHeight > clientHeight means text is truncated)
        if (descriptionParagraph.node().scrollHeight > descriptionParagraph.node().clientHeight) {
          // Content is truncated, add expand arrow after the paragraph
          const expandArrow = descriptionWrapper.append('span')
            .attr('class', 'expand-arrow')
            .html(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 841.04 851.24">
              <path d="M420.52,25.62C199.62,25.62,20.52,204.69,20.52,425.62s179.1,400,400,400,400-179.1,400-400S641.44,25.62,420.52,25.62ZM613.71,335.44c9.85,9.75,9.85,25.58,0,35.35l-174.97,172.48c-9.62,9.52-26.12,9.5-35.72,0l-174.98-172.51c-9.84-9.74-9.84-25.57,0-35.34,9.88-9.78,25.87-9.78,35.73,0l157.12,154.89,157.12-154.87c9.87-9.75,25.84-9.75,35.7,0Z"/>
            </svg>`);

          let isExpanded = false;
          expandArrow.on('click', function () {
            // Handle both D3 v5 (d3.event) and v6+ (event parameter)
            const event = d3.event || arguments[0];
            if (event && event.stopPropagation) {
              event.stopPropagation();
            }

            isExpanded = !isExpanded;
            descriptionWrapper.classed('expanded', isExpanded);
            d3.select(this).classed('flipped', isExpanded);

            // Update iframe height after expansion/collapse
            setTimeout(() => {
              if (window.pymChild) {
                window.pymChild.sendHeight();
              }
            }, 350); // Wait for CSS transition to complete (300ms + buffer)
          });
        }
      };

      // Check height after a brief delay to ensure text is rendered
      setTimeout(checkHeight, 0);

      // Map container
      stateDiv.append('div')
        .attr('class', 'map-container');

      // Add event listeners to each state div to stop auto-scroll on interaction
      const stateDivNode = stateDiv.node();
      stateDivNode.addEventListener('mouseenter', () => stopAutoScroll(false));
      stateDivNode.addEventListener('mouseleave', resumeAutoScroll);
      stateDivNode.addEventListener('wheel', () => {
        stopAutoScroll(true); // Permanently stop on any interaction
      });
      stateDivNode.addEventListener('mousedown', () => {
        stopAutoScroll(true); // Permanently stop on any interaction
      });

      // Mobile-specific events - always permanently stop autoscroll
      stateDivNode.addEventListener('touchstart', () => {
        stopAutoScroll(true);
      });
      stateDivNode.addEventListener('touchmove', () => {
        stopAutoScroll(true);
      });

      // Render initial map (start with 2025)
      renderMap(stateDiv, stateAbbr, true);
    };

    // Create state divs for all states
    displayStates.forEach((stateData) => {
      createStateDiv(stateData, false);
    });

    // Scroll positioning variables
    const stateDivWidth = 430;
    const gap = 20;
    const singleItemWidth = stateDivWidth + gap;

    // Function to center a specific state
    const centerState = (index, useSmooth = true) => {
      // Get all state divs
      const stateDivs = containerNode.querySelectorAll('.state-div');
      if (stateDivs[index]) {
        const stateDiv = stateDivs[index];
        const stateDivRect = stateDiv.getBoundingClientRect();

        // Calculate how much to scroll to center this div
        // Position of div relative to container - half viewport width + half div width
        const scrollOffset = stateDiv.offsetLeft - (containerNode.clientWidth / 2) + (stateDivRect.width / 2);

        isProgrammaticScroll = true;

        if (useSmooth) {
          // Temporarily enable smooth scrolling
          containerNode.style.scrollBehavior = 'smooth';
          containerNode.scrollTo({
            left: scrollOffset,
            behavior: 'smooth',
          });
          // Reset flag and restore auto scroll behavior after smooth scroll completes
          setTimeout(() => {
            containerNode.style.scrollBehavior = 'auto';
            isProgrammaticScroll = false;
          }, 1000);
        } else {
          // Instant scroll
          containerNode.scrollLeft = scrollOffset;
          setTimeout(() => {
            isProgrammaticScroll = false;
          }, 50);
        }
      }
    };

    // Add dropdown change event listener
    dropdown.on('change', function () {
      const selectedValue = this.value;

      // If the default "State" option is selected, resume auto-scroll
      if (selectedValue === '') {
        isStateSelected = false;
        isUserInteracting = false;
        autoScrollPosition = null;
        return;
      }

      // A specific state was selected, pause auto-scroll and center that state
      const selectedIndex = parseInt(selectedValue);
      isStateSelected = true;
      stopAutoScroll(false); // Don't use permanent flag here, as isStateSelected is already set
      centerState(selectedIndex);
      // Don't resume auto-scroll - keep it paused until "State" is selected again
    });

    // Get the position to reset to (first state)
    const getResetPosition = () => {
      const firstState = containerNode.querySelector('.state-div');
      return firstState ? firstState.offsetLeft : 0;
    };

    // Continuous auto-scroll with bounce (reverse at boundaries)
    const autoScroll = () => {
      if (!isUserInteracting && containerNode) {
        if (autoScrollPosition === null) {
          autoScrollPosition = containerNode.scrollLeft;
        }
        autoScrollPosition += scrollSpeed * scrollDirection;

        const maxScroll = Math.max(0, containerNode.scrollWidth - containerNode.clientWidth);
        autoScrollPosition = Math.max(0, Math.min(maxScroll, autoScrollPosition));

        const resetPos = getResetPosition();

        // Set boundaries with small buffer
        const rightBoundary = maxScroll - 50;
        const leftBoundary = resetPos + 50;

        if (scrollDirection === 1 && autoScrollPosition >= rightBoundary) {
          scrollDirection = -1;
        } else if (scrollDirection === -1 && autoScrollPosition <= leftBoundary) {
          scrollDirection = 1;
        }

        containerNode.scrollLeft = autoScrollPosition;
      }
      animationId = requestAnimationFrame(autoScroll);
    };

    // Add drag-to-scroll for desktop
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    containerNode.addEventListener('mousedown', (e) => {
      // Only enable drag on the container itself, not on interactive elements
      if (e.target.closest('button, select, a, .expand-arrow')) {
        return;
      }
      isDragging = true;
      startX = e.pageX - containerNode.offsetLeft;
      scrollLeft = containerNode.scrollLeft;
      containerNode.style.cursor = 'grabbing';
      containerNode.style.userSelect = 'none';
      stopAutoScroll(true); // Permanently stop on drag
    });

    containerNode.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - containerNode.offsetLeft;
      const walk = (x - startX) * 1.5; // Multiply by 1.5 for faster scroll
      containerNode.scrollLeft = scrollLeft - walk;
    });

    containerNode.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        containerNode.style.cursor = 'grab';
        containerNode.style.userSelect = '';
      }
    });

    // Add event listeners
    containerNode.addEventListener('mouseenter', () => stopAutoScroll(false));
    containerNode.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        containerNode.style.cursor = 'grab';
        containerNode.style.userSelect = '';
      }
      resumeAutoScroll();
    });
    containerNode.addEventListener('touchstart', () => stopAutoScroll(true)); // Permanently stop
    containerNode.addEventListener('wheel', () => {
      stopAutoScroll(true); // Permanently stop on wheel
    });

    // Initial scroll position to center the emphasized state
    setTimeout(() => {
      // Find the state with state_emphasis: true (handle boolean, "true", "TRUE", etc.)
      const emphasizedIndex = displayStates.findIndex((state) => {
        if (typeof state.state_emphasis === 'boolean') {
          return state.state_emphasis === true;
        }
        return String(state.state_emphasis).toUpperCase() === 'TRUE';
      });
      const indexToCenter = emphasizedIndex !== -1 ? emphasizedIndex : 0;

      // Use instant scroll for initial centering (no smooth)
      centerState(indexToCenter, false);

      // Start auto-scrolling after initial positioning (disabled on mobile)
      setTimeout(() => {
        isUserInteracting = false;
        isProgrammaticScroll = false;
        if (!isMobile) {
          autoScroll();
        }
      }, 500);
    }, 100);
    if (window.pymChild) {
      window.pymChild.sendHeight();
    }
  } // End initVisualization
}
